import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  Wallet, Bell, LayoutGrid, Globe, Headphones, History, Settings, Bot, Calculator
} from "lucide-react";

// Components
import Sidebar from "../components/dashboard/Sidebar";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import BalanceCards from "../components/dashboard/BalanceCards";
import StatsPanel from "../components/dashboard/StatsPanel";
import TradesTable from "../components/dashboard/TradesTable";
import SupportChat from "../components/SupportChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState({ total_investment: 0, total_income: 0, closed_income: 0, roi: 0, trades_count: 0 });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("dashboard");
  const [chatOpen, setChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Notifications state
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [readNotifIds, setReadNotifIds] = useState(new Set());

  const menuItems = [
    { id: "dashboard", label: "Дашборд", icon: LayoutGrid },
    { id: "wallet", label: "Кошелёк", icon: Wallet, link: "/wallet" },
    { id: "market", label: "Онлайн-рынок", icon: Globe, link: "/market" },
    { id: "calculator", label: "Калькулятор", icon: Calculator, link: "/calculator" },
    { id: "ai-bot", label: "ИИ-бот", icon: Bot, link: "/ai-bot" },
    { id: "support", label: "Поддержка", icon: Headphones, action: "openChat" },
    { id: "history", label: "История", icon: History, link: "/history" },
    { id: "notifications", label: "Уведомления", icon: Bell, link: "/notifications" },
    { id: "settings", label: "Настройки", icon: Settings, link: "/settings" },
  ];

  const fetchData = async () => {
    try {
      const [tradesRes, statsRes] = await Promise.all([
        axios.get(`${API}/trades`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTrades(tradesRes.data);
      setStats(statsRes.data);
      await refreshUser();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/chat/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessages(res.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notifsRes, userRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setNotifications(notifsRes.data);
      const readIds = new Set(userRes.data.read_notifications || []);
      setReadNotifIds(readIds);
      const unread = notifsRes.data.filter(n => !readIds.has(n.id)).length;
      setUnreadNotifications(unread);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReadNotifIds(prev => new Set([...prev, notifId]));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchUnreadCount();
    fetchNotifications();
    
    // Refresh trades every 5 seconds for live PnL updates
    const tradesInterval = setInterval(fetchData, 5000);
    const unreadInterval = setInterval(fetchUnreadCount, 10000);
    const notifsInterval = setInterval(fetchNotifications, 15000);
    
    return () => {
      clearInterval(tradesInterval);
      clearInterval(unreadInterval);
      clearInterval(notifsInterval);
    };
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleMenuClick = (item) => {
    setActiveMenu(item.id);
    if (item.action === "openChat") {
      setChatOpen(true);
      setUnreadMessages(0);
    } else if (item.link) {
      navigate(item.link);
    }
    setSidebarOpen(false);
  };

  const handleChatOpen = () => {
    setChatOpen(true);
    setUnreadMessages(0);
  };

  const totalBalance = (user?.balance || 0) + (user?.trading_balance || 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] flex items-center justify-center">
        <div className="text-[#EDEDED]">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bybit-grid-subtle flex">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        menuItems={menuItems}
        activeMenu={activeMenu}
        sidebarOpen={sidebarOpen}
        unreadMessages={unreadMessages}
        unreadNotifications={unreadNotifications}
        onMenuClick={handleMenuClick}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <DashboardHeader
          user={user}
          totalBalance={totalBalance}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          notificationsOpen={notificationsOpen}
          setNotificationsOpen={setNotificationsOpen}
          notifications={notifications}
          unreadNotifications={unreadNotifications}
          readNotifIds={readNotifIds}
          unreadMessages={unreadMessages}
          onMarkNotificationRead={markNotificationRead}
          onNavigate={navigate}
          onChatOpen={handleChatOpen}
        />

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <BalanceCards user={user} stats={stats} />

          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <StatsPanel stats={stats} />
            </div>

            <div className="lg:col-span-3">
              <TradesTable trades={trades} />
            </div>
          </div>
        </main>
      </div>

      {/* Support Chat Modal */}
      <SupportChat 
        isOpen={chatOpen} 
        onClose={() => setChatOpen(false)} 
        token={token}
      />
    </div>
  );
};

export default Dashboard;
